import { isAfter, isBefore } from 'date-fns';

import type { Reservation } from '@/api';

export type CancelState =
  | 'partial-refund'
  | 'fully-refundable'
  | 'non-refundable-grace'
  | 'non-refundable';

export type CancellationScenario =
  | 'Refundable - Within Window'
  | 'Refundable - Window Closed'
  | 'Non-Refundable - Within Booking Hour'
  | 'Regular';

export function getCancellationScenario(
  cancelState: CancelState
): CancellationScenario {
  switch (cancelState) {
    case 'fully-refundable':
      return 'Refundable - Within Window';
    case 'partial-refund':
      return 'Refundable - Window Closed';
    case 'non-refundable-grace':
      return 'Non-Refundable - Within Booking Hour';
    case 'non-refundable':
    default:
      return 'Regular';
  }
}

export function getCancelState(
  reservation: Reservation,
  now: Date = new Date()
): CancelState {
  if (!reservation.cancellationPolicy) {
    return 'non-refundable';
  }

  const cancelUntilDate = new Date(reservation.cancellationPolicy.cancelUntil);
  const beforeDeadline = isBefore(now, cancelUntilDate);
  const afterDeadline = isAfter(now, cancelUntilDate);
  const { title } = reservation.cancellationPolicy;

  if (
    title === 'Fully Refundable' &&
    afterDeadline &&
    isBefore(now, reservation.startTime)
  ) {
    return 'partial-refund';
  }
  if (title === 'Fully Refundable' && beforeDeadline) {
    return 'fully-refundable';
  }
  if (title === 'Non-Refundable' && beforeDeadline) {
    return 'non-refundable-grace';
  }

  return 'non-refundable';
}

// Extras are refunded all-or-nothing: fully refunded in the full-refund
// window, fully withheld otherwise. This mirrors the backend's
// Reservation#get_extras_cost.
export function getExtrasCost(reservation: Reservation): number {
  return reservation.extras.reduce((sum, extra) => {
    if (extra.tag === 'custom_delivery') return sum;

    return (
      sum +
      (extra.recurring ? reservation.numDays * extra.amount : extra.amount)
    );
  }, 0);
}
